<template>
  <div
    data-component-group="business-form"
    data-component="free-impressions"
    class="free-impressions"
  >
    <slot name="beforeForm" />

    <form
      v-if="!isDisabled"
      novalidate
      @submit.prevent="onUpdate"
    >
      <div class="component-header">
        <magic-container>
          <v-autocomplete
            v-model="country"
            :items="enabledPromotionsCountries"
            prepend-inner-icon="public"
            :label="$t('country')"
            item-title="name"
            item-value="code"
            :return-object="true"
            required
            :readonly="isDisabled"
            :disabled="!hasEnabledPromotionsCountries"
            :error-messages="countryErrors"
            @blur="v$.country.$touch()"
          />

          <v-text-field
            v-model.number="impressions"
            :label="$t('monthly_limit')"
            :error-messages="impressionsErrors"
            required
            autocomplete="off"
            @blur="v$.impressions.$touch()"
          />

          <month-select
            :date="endDate"
            :placeholder="$t('until_end_of')"
            :min-date="getMinDate()"
            :hide-details="false"
            :error-messages="endDateErrors"
            :is-disabled="isDisabled"
            :on-change="(date) => endDate = date"
            :on-clear="() => endDate = ''"
          />
        </magic-container>
      </div>
      <div class="component-actions">
        <Can
          action="update"
          subject="business"
        >
          <v-btn
            color="secondary"
            type="submit"
            :loading="isLoading"
          >
            <v-icon>edit</v-icon>
            {{ triggerLabel }}
          </v-btn>
        </Can>
      </div>
    </form>
    <div class="component-body">
      <div class="item-grid-rows" :class="{ 'item-grid-rows--edit': !isRemovableDisabled }">
      <Removables
        v-if="hasItems"
        :items="businessFreeImpressions"
        :is-disabled="isRemovableDisabled"
        :on-remove="onRemoveBusinessFreeImpressions"
      >
        <template v-slot:item="scope">
          <div class="item-grid">
            <span class="d-flex align-center">
              <v-icon>public</v-icon>
              <span>{{ scope.item.country.name }}</span>
            </span>

            <span>
              {{ scope.item.impressions }}
            </span>

            <span class="d-flex align-center">
              <v-icon>today</v-icon>
              <span>{{ scope.item.end_date }}</span>
            </span>
          </div>
        </template>
      </Removables>
      </div>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import Removables from '../../lists/Removables';
  import { isObj, isStr, isNum } from '../../../utils';

  export default {
    components: {
      Removables,
    },
    setup () {
      return {
        v$: useVuelidate()
      }
    },
    validations() {
      return {
        country: {
          required,
        },
        impressions: {
          required,
          numeric: {
            $validator: value => isNum(value),
            $message: 'Must be a valid number'
          },
          minValue: {
            $validator: value => value >= 1,
            $message: 'Must be greater than 0'
          }
        },
        endDate: {
          required,
        },
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined,
      },
      triggerLabel: {
        type: String,
        default: 'Add',
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        country: null,
        countries: [],
        impressions: '',
        endDate: '',
        isLoading: false,
        isLoadingCountries: false,
        isLoadingPlacements: false,
      };
    },
    computed: {
      activeBusiness() {
        return this.$store.getters['businesses/activeItem'];
      },
      businessFreeImpressions() {
        const { businessFreeImpressions } = this.activeBusiness || {};
        return businessFreeImpressions || [];
      },
      userCountries() {
        return this.$store.getters['users/countries'] || [];
      },
      enabledPromotionsCountries() {
        const enabledPromotions = this.activeBusiness?.enabledPromotions ?? [];

        return this.userCountries.filter((item) => {
          return enabledPromotions.find(promotionCountry => item.code === promotionCountry.country.code);
        });
      },
      hasItems() {
        return this.businessFreeImpressions.length > 0;
      },
      hasEnabledPromotionsCountries() {
        return this.enabledPromotionsCountries.length > 0;
      },
      canUpdateBusiness() {
        return this.$ability.can('update', 'business');
      },
      isRemovableDisabled() {
        return this.isDisabled || !this.canUpdateBusiness;
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('country_is_required');
          }
          return error.$message;
        });
      },
      impressionsErrors() {
        return this.v$.impressions.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('impressions_required');
          }
          if (error.$validator === 'numeric') {
            return this.$t('impressions_numeric');
          }
          if (error.$validator === 'minValue') {
            return this.$t('impressions_positive');
          }
          return error.$message;
        });
      },
      endDateErrors() {
        return this.v$.endDate.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('end_date_required');
          }
          return error.$message;
        });
      },
      hasFormErrors() {
        const { countryErrors, impressionsErrors, endDateErrors } = this;
        return (
          countryErrors.length > 0 ||
          impressionsErrors.length > 0 ||
          endDateErrors.length > 0
        );
      },
    },
    watch: {
      'business.id': {
        immediate: true,
        handler(nextId, prevId) {
          if (nextId) {
            if (prevId !== undefined) {
              this.clear();
            }
            this.getBusinessFreeImpressions(this.business);
          }
        }
      },
    },
    methods: {
      getMinDate() {
        return this.$dayjs().format('YYYY-MM');
      },
      getBusinessFreeImpressions({ id }) {
        const query = { businessId: id };
        return this.$store.dispatch('businesses/getBusinessFreeImpressions', query);
      },
      getEnabledPromotionsCountries({ id }) {
        const query = {
          id,
          only_allowed_countries: true,
        };
        return this.$store.dispatch('businesses/getEnabledPromotions', query);
      },
      canSubmitRequest() {
        return (
          isObj(this.country) &&
          isStr(this.country.code) &&
          isNum(this.business?.id) &&
          isNum(this.impressions) &&
          isStr(this.endDate) &&
          !this.hasFormErrors
        );
      },
      onUpdate() {
        if (this.isLoading) return;

        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error) {
          return false;
        }

        this.isLoading = true;
        const { business, country, impressions, endDate } = this;
        const query = {
          id: business.id,
          country: country.code,
          impressions,
          end_date: endDate,
        };

        return Promise.resolve(this.onSubmit(query))
          .then((res) => {
            this.clear();
            return res;
          })
          .catch(err => err)
          .finally(() => {
            this.isLoading = false;
          });
      },
      onRemoveBusinessFreeImpressions(item) {
        const query = {
          businessId: this.business.id,
          record: item,
        };

        return this.$store.dispatch('businesses/removeBusinessFreeImpressions', query);
      },
      clear() {
        this.v$.$reset();
        this.country = null;
        this.impressions = '';
        this.endDate = '';
        this.isLoadingCountries = false;
        this.isLoadingPlacements = false;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

.free-impressions {
  padding: $spacing-xl;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    padding: 6px 0 16px 0;
    text-align: right;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .removables {
    padding: 16px 0 0 0;

    >.v-list {
      padding: 0 !important;
    }

    .v-list-item.removable-item {
      height: 62px;
      border-radius: $border-radius;
      background-color: rgba(var(--v-theme-on-surface), 0.06);
    }
  }
}
</style>
